import { Pressable, ScrollView, Text, View } from 'react-native';

import { useFollowSelected } from '../primitives/ChipRowFrame';
import type { PickerStyles } from './pickerStyles';
import type { PickerTab } from './useExercisePickerState';
import { stateAria } from "../a11y";

// The picker's tab strip. Extracted from ExercisePicker (PE-40): the file was
// at the 500 cap and the Workout tab's body had to fit somewhere.
//
// ⛔ WHICH tabs exist is decided by the caller and passed in, never derived
// here from a flag: the Workout tab is gated on the host supplying its data, and
// a component that reconstructed that rule would be a second place for it to be
// wrong.
const TAB_KEY: Record<PickerTab, string> = {
  exercises: 'mobile.logger.tabExercises',
  superset: 'mobile.logger.tabSuperset',
  workout: 'composer.tabPlan',
  program: 'library.tabPrograms',
};
const TAB_FALLBACK: Record<PickerTab, string> = {
  exercises: 'Exercises',
  superset: 'Superset',
  workout: 'Plan',
  program: 'Programs',
};

export function PickerTabBar({
  styles,
  t,
  tabs,
  active,
  onSelect,
}: {
  styles: PickerStyles;
  t: (key: string, opts?: Record<string, unknown>) => string;
  tabs: readonly PickerTab[];
  active: PickerTab;
  onSelect: (tab: PickerTab) => void;
}) {
  // #336: four tabs (the program editor's picker) do not fit 360 pt with
  // their words whole, in ru and de, or at a large font. Then the bar scrolls
  // sideways, every tab at its natural width with its full label, and the
  // selected one is kept in view (useFollowSelected, ChipRowFrame's own).
  // Three tabs (the logger): the bar exactly as before.
  const scroll = tabs.length > 3;
  const follow = useFollowSelected<PickerTab>(scroll ? active : null);
  const items = tabs.map((tb) => {
    const selected = active === tb;
    const tab = (
      <Pressable
        key={tb}
        accessibilityRole="button"
        accessibilityState={{ selected }}
        {...stateAria({ selected }, "button")}
        onPress={() => onSelect(tb)}
        hitSlop={{ top: 8, bottom: 8 }}
        style={[styles.tab, scroll && styles.tabNatural, selected && styles.tabActive]}
      >
        <Text style={[styles.tabText, selected && styles.tabTextActive]}>
          {t(TAB_KEY[tb], { defaultValue: TAB_FALLBACK[tb] })}
        </Text>
      </Pressable>
    );
    return scroll ? (
      <View key={tb} onLayout={follow.onChipLayout(tb)}>
        {tab}
      </View>
    ) : (
      tab
    );
  });
  if (!scroll) return <View style={styles.tabBar}>{items}</View>;
  return (
    <ScrollView
      ref={follow.ref}
      horizontal
      showsHorizontalScrollIndicator={false}
      onLayout={follow.onViewport}
      onScroll={follow.onScroll}
      scrollEventThrottle={32}
      style={styles.tabScroller}
      contentContainerStyle={[styles.tabBar, styles.tabBarScroll]}
    >
      {items}
    </ScrollView>
  );
}
